import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { CalendarIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import {
  Building,
  AlertCircle,
  Trash2,
  Loader2,
  Plus,
  Edit,
  Link2,
  Check,
  SendHorizontal,
  Copy,
  Link,
  Activity,
  Settings,
  Shield,
  Bot,
  Bell,
  Landmark,
  Search,
  FlaskConical,
  Pencil,

} from 'lucide-react';
import { checkUserRole } from '@/utils/adminUtils';
import { AdminNav } from '@/components/admin/AdminNav';
import { format } from 'date-fns';
import { Json } from '@/integrations/supabase/types';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import MultiStepOrganisationModal from '@/components/organisation/MultiStepOrganisationModal';
import { sendMagicLinkEmail, LinkType } from '@/utils/magic-link';
import { generateSubmissionIdentifier } from '@/utils/magic-link/helpers';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import UserActivityLogs from '@/components/admin/UserActivityLogs';
import MfaResetModal from '@/components/admin/MfaResetModal';
import SystemMessageEditor from '@/components/admin/SystemMessageEditor';
import SchemaManagementEditor from '@/components/admin/SchemaManagementEditor';
import AlertsManagement from '@/components/admin/AlertsManagement';
import { LenderManagement } from '@/components/admin/LenderManagement';
import { EntityManagement } from '@/components/admin/entities/EntityManagement';
import ConnectionSettings from '@/components/admin/ConnectionSettings';
import { ExportNotificationsProvider } from '@/components/data-table/ExportNotificationsProvider';
import { exportMonitoringService } from "@/utils/organization/exportMonitoringService";
import { initializeExportCleanup } from "@/utils/organization/exportCleanup";
import { monitorExportHealth } from '@/utils/organization/exportCleanupService';
import { featureFlags } from '@/utils/featureFlags';
import MerchantTypeSelector, { MerchantType } from '@/components/admin/MerchantTypeSelector';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

interface Organisation {
  address: string;
  connections: Json;
  created_at: string;
  ein: string;
  id: string;
  name: string;
  name_dbas: string;
  poc_email: string;
  poc_name: string;
  poc_phone?: string;
  products: Json;
  state_of_incorporation: string;
  updated_at?: string;
  status?: string;
  isDraft?: boolean;
  draftId?: string;
  merchant_type: string;
  is_test_clone?: boolean;
  source_organisation_id?: string;
  data_cutoff_date?: string;
}

const Admin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [organisationsCount, setOrganisationsCount] = useState(0);
  const [activeTab, setActiveTab] = useState('organisations');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editOrganizationId, setEditOrganizationId] = useState<string | undefined>(undefined);
  const [isEditingDraft, setIsEditingDraft] = useState(false);

  // Magic link states - store complete organization object to prevent changes from autofill
  const [magicLinkDialogOpen, setMagicLinkDialogOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [selectedOrgForLink, setSelectedOrgForLink] = useState<Organisation | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [useAlternativeEmail, setUseAlternativeEmail] = useState(false);
  const [alternativeEmail, setAlternativeEmail] = useState('');
  const [debouncedAlternativeEmail, setDebouncedAlternativeEmail] = useState('');
  const [submissionIdentifier, setSubmissionIdentifier] = useState('');
  const [linkDestination, setLinkDestination] = useState<LinkType>('onboarding');
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const formatDateTimeLocal = (date: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Convert a local datetime string (no timezone) to a UTC ISO string for edge functions.
  // This prevents a 1-hour display offset when the admin is in BST (UTC+1).
  const toUtcIsoString = (localDateTimeStr?: string): string | undefined =>
    localDateTimeStr ? new Date(localDateTimeStr).toISOString() : undefined;

  const getMinimumResellerExpiry = () => {
    const date = new Date();
    date.setSeconds(0, 0);
    date.setMinutes(date.getMinutes() + 1);
    return date;
  };

  const getMinimumResellerExpiryValue = () => formatDateTimeLocal(getMinimumResellerExpiry());

  const clampResellerExpiry = (value: string) => {
    if (!value) return getMinimumResellerExpiryValue();

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return getMinimumResellerExpiryValue();

    const minimum = getMinimumResellerExpiry();
    return parsed < minimum ? formatDateTimeLocal(minimum) : value;
  };

  const defaultResellerExpiresAt = () => {
    const date = getMinimumResellerExpiry();
    date.setHours(date.getHours() + 24);
    return formatDateTimeLocal(date);
  };

  const [resellerExpiresAt, setResellerExpiresAt] = useState(defaultResellerExpiresAt);
  const [minimumResellerExpiresAt, setMinimumResellerExpiresAt] = useState(getMinimumResellerExpiryValue);

  // Debounce alternative email input (300ms) so validation doesn't run on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedAlternativeEmail(alternativeEmail);
    }, 300);
    return () => clearTimeout(handle);
  }, [alternativeEmail]);

  useEffect(() => {
    if (!magicLinkDialogOpen || linkDestination !== 'reseller_portal') return;

    const syncMinimumExpiry = () => {
      const nextMinimum = getMinimumResellerExpiryValue();
      setMinimumResellerExpiresAt(nextMinimum);
      setResellerExpiresAt((current) => clampResellerExpiry(current));
    };

    syncMinimumExpiry();
    const intervalId = window.setInterval(syncMinimumExpiry, 30000);

    return () => window.clearInterval(intervalId);
  }, [magicLinkDialogOpen, linkDestination]);

  // MFA reset states
  const [mfaResetModalOpen, setMfaResetModalOpen] = useState(false);
  const [selectedOrgForMfaReset, setSelectedOrgForMfaReset] = useState<Organisation | null>(null);

  // E2E test bypass detection via Playwright's navigator.webdriver
  const isAutomatedE2E = (typeof navigator !== 'undefined' && (navigator as any).webdriver)
    && typeof window !== 'undefined';

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (isAutomatedE2E) {
          // In E2E mode, grant admin access directly (test user has admin role in DB)
          setIsAdmin(true);
          setLoading(false);
          return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          navigate('/login');
          return;
        }

        const isUserAdmin = await checkUserRole(user.email || '', 'admin');
        setIsAdmin(isUserAdmin);

        if (!isUserAdmin) {
          navigate('/');
          setLoading(false);
          return;
        }
        // loading will be managed by fetchOrganisationsAndDrafts
      } catch (error) {
        setError('Failed to verify admin status');
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [navigate, isAutomatedE2E]);

  const fetchOrganisationsAndDrafts = async () => {
    if (!isAdmin) return;

    // E2E tests fetch real organizations - global setup creates test data
    try {
      setLoading(true);

      // Fetch submitted organisations
      const { data: submittedOrgs, error: orgsError, count: orgsCount } = await supabase
        .from('organisations')
        .select('*', { count: 'exact' });

      if (orgsError || !submittedOrgs) throw (orgsError || new Error('Failed to load organisations and drafts'));

      // Process submitted organisations
      const processedSubmittedOrgs: Organisation[] = (submittedOrgs || []).map((org: any) => ({
        address: org.address,
        connections: org.connections,
        created_at: org.created_at,
        ein: org.ein,
        id: org.id,
        name: org.name,
        name_dbas: org.name_dbas || '',
        poc_email: org.poc_email,
        poc_name: org.poc_name,
        poc_phone: org.poc_phone || undefined,
        products: org.products,
        state_of_incorporation: org.state_of_incorporation,
        status: org.status || 'submitted',
        isDraft: false,
        merchant_type: org.merchant_type || 'direct',
        is_test_clone: org.is_test_clone || false,
        source_organisation_id: org.source_organisation_id || undefined
      }));

      // Sort by most recent update
      processedSubmittedOrgs.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB.getTime() - dateA.getTime();
      });

      setOrganisations(processedSubmittedOrgs);
      setOrganisationsCount((orgsCount || 0));
    } catch (error) {
      setError('Failed to load organisations and drafts');
      toast({
        title: 'Error',
        description: 'Failed to load organisations and drafts',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchOrganisationsAndDrafts();

      // Initialize export services for admin dashboard
      initializeExportCleanup();
      exportMonitoringService.startAutomaticCleanup();

      // Start periodic health monitoring (skip in E2E tests)
      let healthMonitorInterval: NodeJS.Timeout;
      if (!navigator.webdriver) {
        healthMonitorInterval = setInterval(monitorExportHealth, 60000);
      }

      return () => {
        exportMonitoringService.stopAutomaticCleanup();
        if (healthMonitorInterval) {
          clearInterval(healthMonitorInterval);
        }
      };
    }
  }, [isAdmin, toast]);

  const refreshData = async (silent: boolean = false) => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      fetchOrganisationsAndDrafts()
      if (!(silent || isAutomatedE2E)) {
        toast({
          title: 'Success',
          description: 'Data refreshed successfully',
        });
      }
    } catch (error) {
      if (!(silent || isAutomatedE2E)) {
        toast({
          title: 'Error',
          description: 'Failed to refresh data',
          variant: 'destructive',
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusBadge = (status: string, isDraft: boolean = false) => {
    if (isDraft || status === 'draft') {
      return <Badge variant="warning" className="ml-2">Draft</Badge>;
    }
    return <Badge variant="default">Submitted</Badge>;
  };

  const handleOrganisationClick = (org: Organisation) => {
    if (org.isDraft) {
      navigate(`/organisation/${org.id}?status=draft`);
    } else {
      navigate(`/organisation/${org.id}`);
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Organisation | null>(null);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [itemToClone, setItemToClone] = useState<Organisation | null>(null);
  const [isCloning, setIsCloning] = useState(false);
  const [cloneCutoffDate, setCloneCutoffDate] = useState<string>('');
  const [cutoffEditOpen, setCutoffEditOpen] = useState(false);
  const [orgForCutoffEdit, setOrgForCutoffEdit] = useState<Organisation | null>(null);
  const [cutoffEditValue, setCutoffEditValue] = useState<string>('');
  const [isSavingCutoff, setIsSavingCutoff] = useState(false);

  const handleDeleteClick = (org: Organisation, e: React.MouseEvent) => {
    e.stopPropagation();
    setItemToDelete(org);
    setDeleteDialogOpen(true);
  };

  const handleCloneAsTestClick = (org: Organisation, e: React.MouseEvent) => {
    e.stopPropagation();
    setItemToClone(org);
    setCloneCutoffDate('');
    setCloneDialogOpen(true);
  };

  const confirmCloneAsTest = async () => {
    if (!itemToClone) return;

    setIsCloning(true);
    try {
      const { data: sourceOrg, error: fetchError } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', itemToClone.id)
        .single();

      if (fetchError || !sourceOrg) throw new Error('Failed to fetch source organisation');

      const { id, created_at, updated_at, user_id, ...orgFields } = sourceOrg as any;

      const { data: clonedOrg, error: cloneError } = await supabase
        .from('organisations')
        .insert({
          ...orgFields,
          name: `[TEST] ${sourceOrg.name}`,
          is_test_clone: true,
          source_organisation_id: sourceOrg.id,
          status: 'draft',
          snapshot_table_created: false,
          financial_snapshot_table_created: false,
          ...(cloneCutoffDate ? { data_cutoff_date: cloneCutoffDate } : {}),
        })
        .select()
        .single();

      if (cloneError) throw cloneError;

      toast({
        title: 'Test clone created',
        description: `[TEST] ${sourceOrg.name} has been created. Reports will be written to isolated test tables.`,
      });
      setCloneDialogOpen(false);
      setItemToClone(null);
      refreshData();
    } catch (error) {
      console.error('Error cloning organisation:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to clone organisation',
        variant: 'destructive',
      });
    } finally {
      setIsCloning(false);
    }
  };

  const handleEditCutoffClick = (org: Organisation, e: React.MouseEvent) => {
    e.stopPropagation();
    setOrgForCutoffEdit(org);
    setCutoffEditValue(org.data_cutoff_date || '');
    setCutoffEditOpen(true);
  };

  const saveCutoffDate = async () => {
    if (!orgForCutoffEdit) return;
    setIsSavingCutoff(true);
    try {
      const { error } = await supabase
        .from('organisations')
        .update({ data_cutoff_date: cutoffEditValue || null } as any)
        .eq('id', orgForCutoffEdit.id);
      if (error) throw error;
      toast({
        title: 'Cutoff date updated',
        description: cutoffEditValue
          ? `Data cutoff set to ${cutoffEditValue}. Mappings will be filtered on the next report run.`
          : 'Data cutoff removed. All mappings from the source org are now active.',
      });
      setCutoffEditOpen(false);
      setOrgForCutoffEdit(null);
      refreshData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update cutoff date',
        variant: 'destructive',
      });
    } finally {
      setIsSavingCutoff(false);
    }
  };

  const handleEditClick = (org: Organisation, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the row click handler from firing

    // Set edit mode based on whether this is a draft or a regular organization
    setIsEditingDraft(org.isDraft);
    setEditOrganizationId(org.id);
    setEditModalOpen(true);
  };

  const handleGenerateMagicLinkClick = (org: Organisation, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent the row click handler from firing

    // Prevent opening dialog if already open
    if (magicLinkDialogOpen) {
      return;
    }

    // Capture a frozen copy of the organization to prevent mutations from autofill/password managers
    setSelectedOrgForLink({ ...org });
    setEmailSent(false);
    setUseAlternativeEmail(false);
    setAlternativeEmail('');
    setDebouncedAlternativeEmail('');
    setGeneratedLink(null);
    setGeneratingLink(false);
    setCopiedLink(false);
    setMinimumResellerExpiresAt(getMinimumResellerExpiryValue());
    setResellerExpiresAt(defaultResellerExpiresAt());
    // Reset link destination based on merchant type
    setLinkDestination(org.merchant_type === 'reseller' ? 'reseller_portal' : 'onboarding');
    // Generate a new submission identifier when opening the dialog
    setSubmissionIdentifier(generateSubmissionIdentifier());
    setMagicLinkDialogOpen(true);
  };

  const handleMfaResetClick = (org: Organisation, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the row click handler from firing
    setSelectedOrgForMfaReset(org);
    setMfaResetModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!selectedOrgForLink) {
      console.error('[Magic Link] No organization selected');
      toast({
        title: 'Error',
        description: 'No organization selected. Please close and try again.',
        variant: 'destructive',
      });
      return;
    }

    // Defensive validation - ensure the organization has a valid ID
    if (!selectedOrgForLink.id) {
      console.error('[Magic Link] Organization missing ID:', selectedOrgForLink);
      toast({
        title: 'Error',
        description: 'Invalid organization data. Please refresh and try again.',
        variant: 'destructive',
      });
      return;
    }
    const safeResellerExpiresAt = linkDestination === 'reseller_portal'
      ? clampResellerExpiry(resellerExpiresAt)
      : undefined;

    if (linkDestination === 'reseller_portal' && safeResellerExpiresAt !== resellerExpiresAt) {
      setMinimumResellerExpiresAt(getMinimumResellerExpiryValue());
      setResellerExpiresAt(safeResellerExpiresAt);
      toast({
        title: 'Expiry updated',
        description: 'Past times are not allowed. Please use the updated future time.',
      });
      return;
    }

    setIsSendingEmail(true);
    try {
      // Create company data object from the organization
      const companyData = {
        pocName: selectedOrgForLink.poc_name,
        pocEmail: selectedOrgForLink.poc_email,
        pocPhone: selectedOrgForLink.poc_phone || undefined,
        name: selectedOrgForLink.name,
        ein: selectedOrgForLink.ein,
        products: selectedOrgForLink.products as any[] || [],
        organization_id: selectedOrgForLink.id
      };

      const emailAddress = selectedOrgForLink.poc_email;

      const result = await sendMagicLinkEmail(
        emailAddress,
        alternativeEmail,
        companyData,
        false,
        linkDestination,
        toUtcIsoString(safeResellerExpiresAt)
      );

      if (result.success) {
        setEmailSent(true);
        toast({
          title: 'Email Sent',
          description: `Magic link email sent to ${alternativeEmail || emailAddress}`,
        });
      } else {
        console.error('[Magic Link] Failed to send email:', result.error);
        toast({
          title: 'Error',
          description: result.error || 'Failed to send magic link email',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('[Magic Link] Error sending magic link email:', error);
      toast({
        title: 'Error',
        description: 'Failed to send magic link email',
        variant: 'destructive',
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    const orgToDelete = itemToDelete;
    const orgId = orgToDelete.isDraft ? orgToDelete.draftId : orgToDelete.id;

    // Close dialog and optimistically remove from list
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    setOrganisations(prev => prev.filter(org => {
      const id = org.isDraft ? org.draftId : org.id;
      return id !== orgId;
    }));
    setOrganisationsCount(prev => prev - 1);

    toast({
      title: 'Deleting organisation',
      description: `Removing ${orgToDelete.name} and all associated data`,
    });

    // Run deletion in background
    try {
      const { data, error } = await invokeFunction(supabase, 'delete-organization', {
        body: { organizationId: orgId, isDraft: orgToDelete.isDraft }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || `Failed to delete organization ${orgToDelete.name}`);

      toast({
        title: 'Organisation deleted',
        description: `${orgToDelete.name} has been removed`,
      });
      refreshData();
    } catch (error) {
      console.error('Error deleting organization:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete organisation',
        variant: 'destructive',
      });
      // Restore the list since deletion failed
      refreshData();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
              <p className="text-lg text-gray-600">Loading admin dashboard...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-lg text-gray-600">{error}</p>
              <Button
                onClick={() => window.location.reload()}
                className="mt-4"
              >
                Try Again
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const handleSelectInput = (e: React.MouseEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    input.select();
  };

  return (
    <ExportNotificationsProvider enabled={isAdmin}>
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <AdminNav onRefresh={refreshData} isRefreshing={isRefreshing} />
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="organisations" className="flex items-center gap-2">
                <Building className="h-4 w-4" />
                Merchants
              </TabsTrigger>
              {featureFlags.showAdminEntities && (
                <TabsTrigger value="entities" className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Entities
                </TabsTrigger>
              )}
              {featureFlags.showAdminLenders && (
                <TabsTrigger value="lenders" className="flex items-center gap-2">
                  <Landmark className="h-4 w-4" />
                  Lenders
                </TabsTrigger>
              )}
              <TabsTrigger value="activity-logs" data-testid="activity-logs" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                User Activity
              </TabsTrigger>
              {featureFlags.showAdminAlerts && (
                <TabsTrigger value="alerts" className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Alerts
                </TabsTrigger>
              )}
              <TabsTrigger value="fig-bot-settings" data-testid="system-messages" className="flex items-center gap-2">
                <Bot className="h-4 w-4" />
                Fig Bot Settings
              </TabsTrigger>
              <TabsTrigger value="connections" data-testid="connections-tab" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Connections
              </TabsTrigger>
            </TabsList>

            <TabsContent value="organisations" className="space-y-4">
              <div className="flex justify-between items-center mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-1 mr-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-medium text-gray-500">Organisations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-end justify-between">
                        <div className="text-3xl font-bold">{organisationsCount}</div>
                        <Building className="h-8 w-8 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Button
                  onClick={() => setCreateModalOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Create Organisation
                </Button>
              </div>

              <div className="bg-white rounded-md shadow">
                <div className="p-4 border-b flex justify-between items-center">
                  <h2 className="text-xl font-semibold">Registered Organisations</h2>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
                    <Input
                      placeholder="Search by name or contact..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>EIN</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last Updated</TableHead>
                        <TableHead className="w-[150px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const filteredOrgs = organisations.filter(org => {
                          if (!searchTerm.trim()) return true;
                          const search = searchTerm.toLowerCase();
                          return (
                            org.name.toLowerCase().includes(search) ||
                            org.poc_name.toLowerCase().includes(search) ||
                            org.poc_email.toLowerCase().includes(search)
                          );
                        });
                        return filteredOrgs.length > 0 ? (
                          filteredOrgs.map((org) => (
                            <TableRow
                              key={org.id}
                              className={`cursor-pointer ${org.isDraft || org.status === 'draft' ? 'bg-amber-50' : ''}`}
                              onClick={() => handleOrganisationClick(org)}
                            >
                              <TableCell>
                                <div className="flex items-center">
                                  <Avatar className="mr-3">
                                    <AvatarFallback>{(org.name.match(/[a-zA-Z]/) || ['?'])[0].toUpperCase()}</AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <div className="font-medium flex items-center gap-2">
                                      {org.name}
                                      {org.is_test_clone && (
                                        <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300">TEST</Badge>
                                      )}
                                    </div>
                                    {org.name_dbas && (
                                      <div className="text-sm text-gray-500">DBA: {org.name_dbas}</div>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div>{org.poc_name}</div>
                                  <div className="text-sm text-gray-500">{org.poc_email}</div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="font-mono text-sm">{org.ein}</span>
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <MerchantTypeSelector
                                  organisationId={org.id}
                                  organisationName={org.name}
                                  currentType={(org.merchant_type || 'direct') as MerchantType}
                                  onTypeChange={() => refreshData(true)}
                                />
                              </TableCell>
                              <TableCell>
                                {getStatusBadge(org.status || 'submitted', org.isDraft)}
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">
                                {format(new Date(org.updated_at || org.created_at), 'MMM d, yyyy')}
                              </TableCell>

                              <TableCell className="text-right">
                                <div className="flex justify-end items-center space-x-2">
                                  {org.is_test_clone && org.data_cutoff_date && (
                                    <span className="text-xs text-muted-foreground font-mono">
                                      cutoff: {org.data_cutoff_date}
                                    </span>
                                  )}
                                  {org.is_test_clone && (
                                    <button
                                      onClick={(e) => handleEditCutoffClick(org, e)}
                                      className="text-violet-500 hover:text-violet-700"
                                      title="Edit data cutoff date"
                                    >
                                      <Pencil size={16} />
                                    </button>
                                  )}
                                  {!org.is_test_clone && featureFlags.showAdminCloneAsTest && (
                                    <button
                                      onClick={(e) => handleCloneAsTestClick(org, e)}
                                      className="text-emerald-500 hover:text-emerald-700"
                                      title="Clone as test"
                                    >
                                      <FlaskConical size={18} />
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => handleGenerateMagicLinkClick(org, e)}
                                    className="text-indigo-500 hover:text-indigo-700"
                                    title="Generate magic link"
                                  >
                                    <Link2 size={18} />
                                  </button>
                                  <button
                                    data-testid="reset-mfa-user-1"
                                    onClick={(e) => handleMfaResetClick(org, e)}
                                    className="text-amber-500 hover:text-amber-700"
                                    title="Reset user MFA"
                                  >
                                    <Shield size={18} />
                                  </button>
                                  <button
                                    onClick={(e) => handleEditClick(org, e)}
                                    className="text-blue-500 hover:text-blue-700"
                                    title="Edit organization"
                                  >
                                    <Edit size={18} />
                                  </button>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          onClick={(e) => handleDeleteClick(org, e)}
                                          className={"text-red-500 hover:text-red-700"}
                                        >
                                          <Trash2 size={18} />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{'Delete organization'}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                              {searchTerm ? 'No organisations match your search' : 'No organisations found'}
                            </TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {featureFlags.showAdminEntities && (
              <TabsContent value="entities" className="space-y-4">
                <EntityManagement />
              </TabsContent>
            )}

            {featureFlags.showAdminLenders && (
              <TabsContent value="lenders" className="space-y-4">
                <LenderManagement />
              </TabsContent>
            )}

            <TabsContent value="activity-logs" className="space-y-4">
              <UserActivityLogs />
            </TabsContent>

            {featureFlags.showAdminAlerts && (
              <TabsContent value="alerts" className="space-y-4">
                <AlertsManagement />
              </TabsContent>
            )}

            <TabsContent value="fig-bot-settings" className="space-y-4">
              <div className="space-y-6">
                {/* System Settings Section */}
                <div className="bg-white rounded-md shadow">
                  <div className="p-4 border-b">
                    <h2 className="text-xl font-semibold">System Configuration</h2>
                    <p className="text-gray-600 mt-1">Manage AI assistant system messages and global settings</p>
                  </div>
                  <div className="p-6">
                    <SystemMessageEditor />
                  </div>
                </div>

                {/* Schema Management Section */}
                <div className="bg-white rounded-md shadow">
                  <div className="p-4 border-b">
                    <h2 className="text-xl font-semibold">Schema Management</h2>
                    <p className="text-gray-600 mt-1">Manage database schema configurations and data connections</p>
                  </div>
                  <div className="p-6">
                    <SchemaManagementEditor />
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="connections" className="space-y-4">
              <ConnectionSettings />
            </TabsContent>

          </Tabs>
        </main>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this organization?
                This will permanently delete all associated files, CSV imports, and data.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-red-500 hover:bg-red-600"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Clone as Test Dialog */}
        <Dialog open={cloneDialogOpen} onOpenChange={setCloneDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create Test Clone</DialogTitle>
              <DialogDescription>
                Create a test clone of <strong>{itemToClone?.name}</strong>. All settings and
                connections will be copied but reports will be written to isolated test tables.
                The clone can be safely deleted when testing is complete.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <Label htmlFor="cutoff-date">Data Cutoff Date <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="cutoff-date"
                type="date"
                value={cloneCutoffDate}
                onChange={(e) => setCloneCutoffDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground">
                Only products sold on or before this date will be recognised on this test clone.
                This allows you to simulate how the system would have behaved at a specific point in time.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCloneDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={confirmCloneAsTest}
                disabled={isCloning}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {isCloning ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cloning...
                  </>
                ) : (
                  <>
                    <FlaskConical className="mr-2 h-4 w-4" />
                    Clone as Test
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Data Cutoff Date Dialog */}
        <Dialog open={cutoffEditOpen} onOpenChange={setCutoffEditOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Data Cutoff Date</DialogTitle>
              <DialogDescription>
                Set the cutoff date for <strong>{orgForCutoffEdit?.name}</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <Label htmlFor="cutoff-edit-date">Cutoff Date</Label>
              <Input
                id="cutoff-edit-date"
                type="date"
                value={cutoffEditValue}
                onChange={(e) => setCutoffEditValue(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to remove the cutoff.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCutoffEditOpen(false)}>Cancel</Button>
              <Button onClick={saveCutoffDate} disabled={isSavingCutoff}>
                {isSavingCutoff ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={magicLinkDialogOpen} onOpenChange={setMagicLinkDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Magic Link for {selectedOrgForLink?.name}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="flex items-center space-x-2 mb-4">
                <Checkbox
                  id="use-alternative-email"
                  checked={useAlternativeEmail}
                  onCheckedChange={(checked) => setUseAlternativeEmail(!!checked)}
                />
                <Label htmlFor="use-alternative-email">Send to different email</Label>
              </div>

              {useAlternativeEmail && (
                <div className="space-y-2">
                  <Label htmlFor="alternative-email">Alternative Email</Label>
                  <Input
                    id="alternative-email"
                    type="email"
                    value={alternativeEmail}
                    onChange={(e) => setAlternativeEmail(e.target.value)}
                    placeholder="Enter alternative email address"
                    className="w-full"
                    autoComplete="off"
                  />
                </div>
              )}

              {selectedOrgForLink?.merchant_type === 'reseller' && (
                <div className="space-y-3 pt-2 border-t">
                  <Label>Link Destination</Label>
                  <RadioGroup
                    value={linkDestination}
                    onValueChange={(value) => setLinkDestination(value as LinkType)}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="onboarding" id="onboarding" />
                      <Label htmlFor="onboarding" className="font-normal">Onboarding Dashboard</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="reseller_portal" id="reseller_portal" />
                      <Label htmlFor="reseller_portal" className="font-normal">Reseller Portal (Purchase Orders)</Label>
                    </div>
                  </RadioGroup>
                  {linkDestination === 'reseller_portal' && (() => {
                    const parsedDate = resellerExpiresAt ? new Date(resellerExpiresAt) : new Date();
                    const selectedDate = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
                    const selectedHour = parsedDate.getHours();
                    const selectedMinute = parsedDate.getMinutes();
                    const now = new Date();
                    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const isToday = selectedDate.getTime() === todayStart.getTime();
                    const minHour = isToday ? now.getHours() : 0;
                    const minMinute = isToday && selectedHour === now.getHours() ? now.getMinutes() + 1 : 0;

                    const updateDateTime = (date: Date, hour: number, minute: number) => {
                      const d = new Date(date);
                      d.setHours(hour, minute, 0, 0);
                      setResellerExpiresAt(formatDateTimeLocal(d));
                    };

                    return (
                      <div className="space-y-2 pt-1">
                        <Label>Session expiry date & time</Label>
                        <div className="flex items-start gap-3">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className={cn("flex-1 justify-start text-left font-normal h-10", !resellerExpiresAt && "text-muted-foreground")}>
                                <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                                {resellerExpiresAt ? format(parsedDate, 'MMM d, yyyy') : 'Pick a date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={selectedDate}
                                onSelect={(day) => {
                                  if (!day) return;
                                  const newIsToday = day.getTime() === todayStart.getTime();
                                  let h = selectedHour;
                                  let m = selectedMinute;
                                  if (newIsToday) {
                                    if (h < now.getHours()) { h = now.getHours(); m = now.getMinutes() + 1; }
                                    else if (h === now.getHours() && m <= now.getMinutes()) { m = now.getMinutes() + 1; }
                                    if (m >= 60) { h += 1; m = 0; }
                                  }
                                  updateDateTime(day, h, m);
                                }}
                                disabled={(date) => date < todayStart}
                                initialFocus
                                className={cn("p-3 pointer-events-auto")}
                              />
                            </PopoverContent>
                          </Popover>
                          <div className="flex items-center gap-1.5">
                            <Select
                              value={String(selectedHour)}
                              onValueChange={(v) => {
                                const h = parseInt(v);
                                let m = selectedMinute;
                                if (isToday && h === now.getHours() && m <= now.getMinutes()) { m = now.getMinutes() + 1; }
                                if (m >= 60) m = 0;
                                updateDateTime(selectedDate, h, m);
                              }}
                            >
                              <SelectTrigger className="w-[72px] h-10">
                                <SelectValue placeholder="HH" />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 24 }, (_, i) => i)
                                  .filter((i) => !isToday || i >= minHour)
                                  .map((i) => (
                                    <SelectItem key={i} value={String(i)}>
                                      {String(i).padStart(2, '0')}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <span className="text-muted-foreground font-medium">:</span>
                            <Select
                              value={String(selectedMinute)}
                              onValueChange={(v) => updateDateTime(selectedDate, selectedHour, parseInt(v))}
                            >
                              <SelectTrigger className="w-[72px] h-10">
                                <SelectValue placeholder="MM" />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 60 }, (_, i) => (
                                  <SelectItem key={i} value={String(i)} disabled={isToday && selectedHour === now.getHours() && i < minMinute}>
                                    {String(i).padStart(2, '0')}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {!Intl.DateTimeFormat().resolvedOptions().timeZone.includes('Los_Angeles') && resellerExpiresAt && (
                          <p className="text-xs text-muted-foreground">
                            {new Date(resellerExpiresAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', dateStyle: 'medium', timeStyle: 'short' })} Pacific
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {(() => {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                const trimmedDebounced = (debouncedAlternativeEmail || '').trim();
                const altEmailInvalid = useAlternativeEmail && !emailRegex.test(trimmedDebounced);
                const isPending = useAlternativeEmail && alternativeEmail.trim() !== trimmedDebounced;
                const isDisabled = isSendingEmail || emailSent || altEmailInvalid || isPending || (linkDestination === 'reseller_portal' && clampResellerExpiry(resellerExpiresAt) !== resellerExpiresAt);
                const disabledReason = isPending
                  ? 'Validating email…'
                  : altEmailInvalid
                    ? (!alternativeEmail ? 'Please enter an email address' : 'Please enter a valid email address')
                    : null;

                const button = (
                  <Button
                    onClick={handleSendEmail}
                    disabled={isDisabled}
                    className="w-full"
                  >
                    {isSendingEmail ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : emailSent ? (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Email Sent
                      </>
                    ) : (
                      <>
                        <SendHorizontal className="mr-2 h-4 w-4" />
                        Send Magic Link {useAlternativeEmail ? `to ${alternativeEmail}` : ''}
                      </>
                    )}
                  </Button>
                );

                if (disabledReason) {
                  return (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0} className="block w-full">{button}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{disabledReason}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                }
                return button;
              })()}

              <div className="text-sm text-gray-500 mt-4">
                <p>The magic link will be sent via email to {useAlternativeEmail ? alternativeEmail : selectedOrgForLink?.poc_email}.</p>
                <p className="mt-1">
                  {linkDestination === 'reseller_portal'
                    ? `The link allows access to the Purchase Orders dashboard. Session expires on ${new Date(resellerExpiresAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}.`
                    : "The link allows access to edit this organization's information for 24 hours."}
                </p>
              </div>

              {/* Generate & Copy Link Section */}
              <div className="p-4 border border-dashed border-muted-foreground/30 rounded-lg bg-muted/20">
                <div className="flex items-center gap-2 mb-2">
                  <Link className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">Generate copyable link</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!selectedOrgForLink) return;

                    const safeResellerExpiresAt = linkDestination === 'reseller_portal'
                      ? clampResellerExpiry(resellerExpiresAt)
                      : undefined;

                    if (linkDestination === 'reseller_portal' && safeResellerExpiresAt !== resellerExpiresAt) {
                      setMinimumResellerExpiresAt(getMinimumResellerExpiryValue());
                      setResellerExpiresAt(safeResellerExpiresAt);
                      toast({ title: 'Expiry updated', description: 'Past times are not allowed. Please use the updated future time.' });
                      return;
                    }

                    setGeneratingLink(true);
                    setGeneratedLink(null);
                    try {
                      const recipientEmail = useAlternativeEmail && alternativeEmail ? alternativeEmail : selectedOrgForLink.poc_email;
                      const primaryEmail = selectedOrgForLink.poc_email;
                      const callbackPath = linkDestination === 'reseller_portal' ? '/reseller/auth-callback' : '/auth-callback';
                      const recipientParam = recipientEmail !== primaryEmail ? `&recipient_email=${encodeURIComponent(recipientEmail)}` : '';
                      const redirectTo = `${window.location.origin}${callbackPath}?submission_identifier=${submissionIdentifier}${recipientParam}`;
                      const { data, error } = await invokeFunction(supabase, 'generate-magic-link', {
                        body: {
                          email: primaryEmail,
                          submissionIdentifier,
                          redirectTo,
                          organisationId: selectedOrgForLink.id,
                          linkType: linkDestination,
                          expiresAt: toUtcIsoString(safeResellerExpiresAt),
                          recipientEmail: recipientEmail !== primaryEmail ? recipientEmail : undefined
                        }
                      });
                      if (error || !data?.success) {
                        throw new Error(data?.error || error?.message || 'Failed to generate link');
                      }
                      setGeneratedLink(data.magicLink);
                      toast({ title: "Link generated", description: "Magic link is ready to copy." });
                    } catch (err: any) {
                      toast({ title: "Error", description: err.message || "Failed to generate link", variant: "destructive" });
                    } finally {
                      setGeneratingLink(false);
                    }
                  }}
                  disabled={generatingLink || isSendingEmail || (linkDestination === 'reseller_portal' && clampResellerExpiry(resellerExpiresAt) !== resellerExpiresAt)}
                  className="w-full mb-2"
                >
                  {generatingLink ? "Generating..." : "Generate Link"}
                </Button>
                {generatedLink && (
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={generatedLink}
                      className="text-xs font-mono"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(generatedLink);
                          setCopiedLink(true);
                          toast({ title: "Copied", description: "Magic link copied to clipboard." });
                          setTimeout(() => setCopiedLink(false), 2000);
                        } catch {
                          toast({ title: "Error", description: "Failed to copy link.", variant: "destructive" });
                        }
                      }}
                      title="Copy link"
                    >
                      {copiedLink ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                )}
              </div>

            </div>

            <DialogFooter className="sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setMagicLinkDialogOpen(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Organisation Modal */}
        <MultiStepOrganisationModal
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          onSuccess={refreshData}
        />

        {/* Edit Organisation Modal */}
        <MultiStepOrganisationModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          onSuccess={refreshData}
          editMode={true}
          organizationId={editOrganizationId}
          isDraft={isEditingDraft}
          isAdminEdit={true} // Pass this flag to indicate admin edit mode
        />

        {/* MFA Reset Modal */}
        {selectedOrgForMfaReset && (
          <MfaResetModal
            open={mfaResetModalOpen}
            onOpenChange={setMfaResetModalOpen}
            userEmail={selectedOrgForMfaReset.poc_email}
            userName={selectedOrgForMfaReset.poc_name}
            onSuccess={() => refreshData(true)}
          />
        )}
      </div>
    </ExportNotificationsProvider>
  );
};

export default Admin;
